import {
  AcademicCapIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  HomeIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps } from 'react';

import { UserTypeEnum } from '@/models/User';
import TranslateInterface from '@/translations/translateInterface';
import { EXAM_BASE, ROUTES } from '@/utils/routes';

export enum AppShellNavItemType {
  HOME = 'HOME',
  LESSONS = 'LESSONS',
  EXAMS = 'EXAMS',
  COURSES = 'COURSES',
  PENDENCIES = 'PENDENCIES',
  STUDENTS = 'STUDENTS',
  KLASSES = 'KLASSES',
}

type AppShellNavLabelKey = keyof TranslateInterface['appShell']['nav'];

export type AppShellNavItem = {
  type: AppShellNavItemType;
  href: string;
  activePath: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  labelKey: AppShellNavLabelKey;
};

export type ShellNavItemProps = Pick<
  AppShellNavItem,
  'href' | 'activePath' | 'icon'
> & {
  label: string;
};

type GetAppShellNavItemsParams = {
  userType: UserTypeEnum;
  lessonsHref?: string;
};

const VISIBLE_NAV_ITEMS: Record<AppShellNavItemType, boolean> = {
  [AppShellNavItemType.HOME]: true,
  [AppShellNavItemType.LESSONS]: true,
  [AppShellNavItemType.EXAMS]: false,
  [AppShellNavItemType.COURSES]: true,
  [AppShellNavItemType.PENDENCIES]: true,
  [AppShellNavItemType.STUDENTS]: true,
  [AppShellNavItemType.KLASSES]: true,
};

const prefixOf = (base: string) => `${base}/*`;

const HOME: AppShellNavItem = {
  type: AppShellNavItemType.HOME,
  href: '/',
  activePath: '/',
  icon: HomeIcon,
  labelKey: 'home',
};

const LESSONS: AppShellNavItem = {
  type: AppShellNavItemType.LESSONS,
  href: ROUTES.COURSES.BASE,
  activePath: prefixOf(ROUTES.COURSES.BASE),
  icon: AcademicCapIcon,
  labelKey: 'lessons',
};

const EXAMS: AppShellNavItem = {
  type: AppShellNavItemType.EXAMS,
  href: EXAM_BASE,
  activePath: prefixOf(EXAM_BASE),
  icon: DocumentTextIcon,
  labelKey: 'exams',
};

const COURSES: AppShellNavItem = {
  type: AppShellNavItemType.COURSES,
  href: ROUTES.COURSES.BASE,
  activePath: prefixOf(ROUTES.COURSES.BASE),
  icon: BookOpenIcon,
  labelKey: 'courses',
};

const COURSE_LINES: AppShellNavItem = {
  type: AppShellNavItemType.COURSES,
  href: ROUTES.ADMIN.COURSE_LINES.BASE,
  activePath: prefixOf(ROUTES.ADMIN.COURSE_LINES.BASE),
  icon: BookOpenIcon,
  labelKey: 'courses',
};

const PENDENCIES: AppShellNavItem = {
  type: AppShellNavItemType.PENDENCIES,
  href: ROUTES.PENDENCIES.BASE,
  activePath: prefixOf(ROUTES.PENDENCIES.BASE),
  icon: ClipboardDocumentListIcon,
  labelKey: 'pendencies',
};

const STUDENTS: AppShellNavItem = {
  type: AppShellNavItemType.STUDENTS,
  href: ROUTES.ADMIN.STUDENTS.BASE,
  activePath: prefixOf(ROUTES.ADMIN.STUDENTS.BASE),
  icon: UsersIcon,
  labelKey: 'students',
};

const KLASSES: AppShellNavItem = {
  type: AppShellNavItemType.KLASSES,
  href: ROUTES.ADMIN.CLASSES.BASE,
  activePath: prefixOf(ROUTES.ADMIN.CLASSES.BASE),
  icon: UserGroupIcon,
  labelKey: 'klasses',
};

const NAV_ITEMS_BY_ROLE: Record<UserTypeEnum, AppShellNavItem[]> = {
  [UserTypeEnum.STUDENT]: [HOME, LESSONS, EXAMS],
  [UserTypeEnum.TEACHER]: [HOME, COURSES, PENDENCIES, STUDENTS, KLASSES],
  [UserTypeEnum.SUPER_ADMIN]: [HOME, COURSE_LINES, STUDENTS, KLASSES],
  [UserTypeEnum.UNIT_ADMIN]: [HOME, COURSES, PENDENCIES, STUDENTS, KLASSES],
  [UserTypeEnum.CONTENT_CREATOR]: [HOME, COURSE_LINES],
  [UserTypeEnum.PARENT]: [HOME],
};

const toNavItem = (
  item: AppShellNavItem,
  lessonsHref?: string,
): AppShellNavItem => {
  if (item.type === AppShellNavItemType.LESSONS && lessonsHref)
    return { ...item, href: lessonsHref };

  return item;
};

export function getAppShellNavItems({
  userType,
  lessonsHref,
}: GetAppShellNavItemsParams): AppShellNavItem[] {
  return NAV_ITEMS_BY_ROLE[userType]
    .filter(({ type }) => VISIBLE_NAV_ITEMS[type])
    .map(item => toNavItem(item, lessonsHref));
}
